import { useEffect } from 'react'
import useIntroStore from '../useIntroStore'
import { useQrScanner } from '@/common/CodeScanner/useQrScanner'
import { trpc } from 'api'
import useHeadlessStaffLogin from 'hooks/staff/useHeadlessStaffLogin'
import { QrParserResult } from '@/common/CodeScanner/parseQrCode'
import { useToast } from '@ui/toasts/useToastManager'
import { useTranslation } from 'react-i18next'
import { useStore } from 'hooks/useSession'
import { useDealTypeModalState } from '@order/hooks/useDealTypeModal'

const useStaffQrHandler = () => {
  const addQrCallback = useQrScanner((s) => s.addCallback)
  const closeQrModal = useQrScanner((s) => s.close)
  const mode = useQrScanner((s) => s.mode)
  const store = useStore()
  const { t } = useTranslation(['staff'])
  const reset = useIntroStore((s) => s.reset)
  const closeDealTypeModal = useDealTypeModalState((s) => s.close)

  const toast = useToast()

  const staff = useHeadlessStaffLogin({
    onSuccess: () => {
      reset()
      toast({
        variant: 'success',
        title: t('staff:login.success'),
      })
    },
  })

  const toggleMode = useIntroStore((s) => s.toggleMode)
  const qrCodeCheck = trpc.qr.checkQrCode.useMutation({
    onSuccess: (response) => {
      if (!response) {
        return
      }
      closeQrModal()
      if (response.type === 'staff') {
        if (staff.isValidStore) {
          staff.login(`${response.code}`)
          return
        }

        toggleMode('staff', { qr: response })
      }
    },
  })

  const onQrCodeScanned = async (result: QrParserResult) => {
    if (!result) return
    if (qrCodeCheck.isPending) return
    if (result.mode !== 'staff') return
    if (!store) {
      // Since we cannot fetch staff if we are missing the store data there is no need to do a request if we dont have a store
      closeDealTypeModal()
      toggleMode('staff', {
        redirect: '/deals',
        qr: { code: result.code, type: 'staff' },
      })
      return
    }

    qrCodeCheck.mutate({
      ...result,
      mode: 'staff',
    })
  }

  useEffect(() => {
    // only listen for staff qr codes
    if (mode !== 'staff') return
    const remove = addQrCallback(onQrCodeScanned)
    return () => {
      remove()
    }
  }, [mode])
}

export default useStaffQrHandler
